import type { DraftAttachmentSource } from "./attachmentDraft";

const DB_NAME = "orbit-bridge-drafts";
const DB_VERSION = 1;
const STORE_NAME = "attachments";
const MAX_RECOVERED_ATTACHMENTS = 5;

export interface RecoveredAttachmentRecord {
  localId: string;
  fileName: string;
  contentType: string;
  byteSize: number;
  caption: string;
  source: DraftAttachmentSource;
  createdAt: number;
  blob: Blob;
}

export async function saveRecoverableAttachment(record: RecoveredAttachmentRecord): Promise<void> {
  const db = await openRecoveryDb();
  try {
    await runStoreRequest(db, "readwrite", (store) => store.put(record));
    await pruneRecoveredAttachments(db);
  } finally {
    db.close();
  }
}

export async function listRecoverableAttachments(): Promise<RecoveredAttachmentRecord[]> {
  const db = await openRecoveryDb();
  try {
    const records = await runStoreRequest<RecoveredAttachmentRecord[]>(db, "readonly", (store) => store.getAll());
    return records.sort((a, b) => b.createdAt - a.createdAt).slice(0, MAX_RECOVERED_ATTACHMENTS);
  } finally {
    db.close();
  }
}

export async function removeRecoverableAttachment(localId: string): Promise<void> {
  const db = await openRecoveryDb();
  try {
    await runStoreRequest(db, "readwrite", (store) => store.delete(localId));
  } finally {
    db.close();
  }
}

function openRecoveryDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (!("indexedDB" in globalThis)) {
      reject(new Error("Local attachment recovery is not available in this browser."));
      return;
    }

    const request = globalThis.indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "localId" });
      }
    };
    request.onerror = () => reject(request.error ?? new Error("Failed to open Orbit recovery storage."));
    request.onsuccess = () => resolve(request.result);
  });
}

function runStoreRequest<T = void>(
  db: IDBDatabase,
  mode: IDBTransactionMode,
  createRequest: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, mode);
    const request = createRequest(transaction.objectStore(STORE_NAME));
    request.onerror = () => reject(request.error ?? new Error("Orbit recovery storage request failed."));
    request.onsuccess = () => resolve(request.result);
    transaction.onerror = () => reject(transaction.error ?? new Error("Orbit recovery storage transaction failed."));
  });
}

async function pruneRecoveredAttachments(db: IDBDatabase): Promise<void> {
  const records = await runStoreRequest<RecoveredAttachmentRecord[]>(db, "readonly", (store) => store.getAll());
  const staleRecords = records
    .sort((a, b) => b.createdAt - a.createdAt)
    .slice(MAX_RECOVERED_ATTACHMENTS);

  if (!staleRecords.length) return;
  await runStoreRequest(db, "readwrite", (store) => {
    for (const record of staleRecords) store.delete(record.localId);
    return store.count();
  });
}
